import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, watch, computed } from 'vue';
import type { Category } from '@/ts/console/type/Category';
import type { City } from '@/ts/console/type/City';
import axios from 'axios';
//- お知らせの一覧
type CategoryWithType = Category & { type?: 'news' | 'project' };

export default /*@__PURE__*/_defineComponent({
  __name: 'VueNewsPage',
  setup(__props, { expose: __expose }) {
  __expose();

const newsList = ref<any[]>([]);

const meta = ref({
  current_page: 1,
  last_page: 1,
  from: 0,
  to: 0,
  total: 0,
})

// ページ番号の配列を作る（1 ～ meta.last_page まで）
const pageNumbers = computed(() => {
  const currentPage = meta.value.current_page;
  const lastPage = meta.value.last_page;
  const delta = 2;
  const left = currentPage - delta;
  const right = currentPage + delta + 1;
  const range: (number | string)[] = [];
  const rangeWithDots: (number | string)[] = [];
  let l: number | undefined;

  for (let i = 1; i <= lastPage; i++) {
    if (i === 1 || i === lastPage || (i >= left && i < right)) {
      range.push(i);
    }
  }

  for (const i of range as number[]) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push('...');
      }
    }
    rangeWithDots.push(i);
    l = i;
  }
  return rangeWithDots;
})

//- カテゴリーの一覧（news / project 両方を持つ）
const categoryList = ref<CategoryWithType[]>([]);
//- エリアの一覧
const cityList = ref<any[]>([]);
const page = ref(1);

const baseUrl = (window as any).baseUrl;

// メインタブの状態
// 'category'カテゴリで探すタブ
// 'area'エリアで探すタブ
const activeMainTab = ref<'category' | 'city'>('category');

// どのカテゴリが選択されているか（null = すべて）
const selectedCategoryId = ref<number | null>(null)
// どのエリアが選択されているか（null = すべて）
const selectedCityIds = ref<number[] | null>([])

onMounted(() => {
  fetchNews();
  fetchCategory();
  fetchCity();
})

//- サーバー側からお知らせの一覧を取得
const fetchNews = async () => {
  try {
    const response = await axios.get(`${baseUrl}/api/news`, {
      params: {
        per_page: 10,
        page: page.value,
        category_id: selectedCategoryId.value ?? undefined,
        city_ids: selectedCityIds.value?.length ? selectedCityIds.value : undefined,
      }
    });
    // console.log('お知らせとプロジェクトの一覧', response);
    newsList.value = response.data.data;
    meta.value = response.data.meta;
    // console.log(newsList.value, meta.value);
  } catch (error) {
    console.error('Error fetching news:', error);
  }
}

//- サーバー側からカテゴリの一覧を取得（news / project 両方）
const fetchCategory = async () => {
  try {
    const response = await axios.get(`${baseUrl}/api/category`, {
      params: {
        types: ['news', 'project'],
      }
    });
    // console.log('response', response);
    categoryList.value = (response.data || []).map((c: any) => ({
      id: Number(c.id),
      name: c.name,
      type: c.type,
    }));
    // console.log('categoryList', categoryList.value);
  } catch (error) {
    console.error('Error fetching category:', error);
  }
}

//- サーバー側からエリアの一覧を取得
const fetchCity = async () => {
  try {
    const response = await axios.get(`${baseUrl}/api/city`);

    // console.log('response city', response);

    cityList.value = response.data;
    // console.log('cityList', cityList.value);
  } catch (error) {
    console.error('Error fetching city:', error);
  }
}

// 地域ごとのオブジェクト cityList から、
// 県北 → 県央 → 県南 → その他 の順で市だけを配列にする
const flatCityList = computed(() => {
  // 並び順を決める配列
  const order = ['県北地域', '県央地域', '県南地域', 'その他'];

  const result: { id: number; name: string }[] = [];

  for(const region of order) {
    // cityList.value[region] が存在しない場合は空配列として扱う
    const cities = cityList.value?.[region as any] ?? [];
    // ... で配列を結合
    result.push(...cities);
  }

  return result;
});

/**
 * メインタブ（「カテゴリで探す」 or 「エリアで探す」）がクリックされたときの処理
 */
const handleMainTabClick = (tab: 'category' | 'city') => {
  activeMainTab.value = tab;

  if(tab === 'category'){
    // カテゴリーで探すに切り替えたときはエリアの条件をリセット
    selectedCityIds.value = [];
  } else {
    // エリアで探すに切り替えたときはカテゴリーの条件をリセット
    selectedCategoryId.value = null;

  }
  // タブを切り替えたときはページを1ページ目に戻す
  page.value = 1;
}


const handleCategoryClick = (id: number | null) => {
  selectedCategoryId.value = id;
  page.value = 1;
}

const handleCityClick = (id: number | null) => {
  if (id === null) {
    // 「すべて」選択：条件リセット
    selectedCityIds.value = [];
  } else {
    const ids = selectedCityIds.value ?? [];
    selectedCityIds.value = ids.includes(id) ? ids.filter(v => v !== id) : [...ids, id];
  }
  page.value = 1;
}

// ページ変更で再取得
// watch：
// page または selectedCategoryId が変わった瞬間に fetchNews() を実行する
// → カテゴリ切り替えや次へ/前へボタンで自動的にニュースが再取得される
// immediate: true により「初回表示時」も fetchNews() が 1 回実行される
watch([page, selectedCategoryId, selectedCityIds], fetchNews);

// formatDate：API から返ってきた日時（ISO形式）を「YYYY/MM/DD」形式に整形する
// 例：2025-11-19T01:31:05.000000Z → 2025/11/19
const formatDate = (dateString: string) => {
  if (!dateString) return '-'
  const datePart = dateString.split('T')[0] // "YYYY-MM-DD"
  return datePart.replace(/-/g, '/')
}

const goNext = () => {
  if(meta.value.current_page < meta.value.last_page){
    page.value++;
  }
}

const goPrev = () => {
  if(meta.value.current_page > 1){
    page.value--;
  }
}

// script setupに追加
const linkFor = (item: any) => {
  if (item.external_url) return item.external_url;
  if (item.detail_path) return item.detail_path;
  return item?.type === 'project'
    ? `${baseUrl}/project/${item.id}`
    : `${baseUrl}/news/${item.id}`;
};

// 表示順：news → project の順に並べ、かつ「その他」を常に最後に持ってくるカテゴリリスト
const orderedCategoryList = computed(() => {
  const order = { news: 0, project: 1 } as Record<string, number>;
  const sorted = [...categoryList.value].sort((a, b) => {
    const aOrder = order[a.type ?? 'news'] ?? 0;
    const bOrder = order[b.type ?? 'news'] ?? 0;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return (a.id ?? 0) - (b.id ?? 0);
  });

  // 名前が「その他」のカテゴリを末尾に移動する
  const idx = sorted.findIndex(c => c.name === 'その他');
  if (idx === -1) return sorted;
  const copy = sorted.slice();
  const [others] = copy.splice(idx, 1);
  copy.push(others);
  return copy;
});



const __returned__ = { newsList, meta, pageNumbers, categoryList, cityList, page, baseUrl, activeMainTab, selectedCategoryId, selectedCityIds, fetchNews, fetchCategory, fetchCity, flatCityList, handleMainTabClick, handleCategoryClick, handleCityClick, formatDate, goNext, goPrev, linkFor, orderedCategoryList, ref, onMounted, watch, computed, get axios() { return axios } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})